import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import {
	Box,
	Button,
	Flex,
	Table,
	Tbody,
	Text,
	Th,
	Thead,
	Tr,
	Divider,
	Input,
	Stack,
	FormControl,
	Select,
	useDisclosure,
	Checkbox,
	Textarea,
	useToast,
	Tooltip,
	Td,
	SimpleGrid,
	VStack,
} from "@chakra-ui/react";
import moment from "moment";
import { FiCalendar, FiFileText, FiInfo, FiUpload } from "react-icons/fi";

import {
	MODAL_GRID_SPACING,
	MODAL_SECTION_SPACING,
	ModalFieldLabel,
	ModalFooterActions,
	PropertyModalSection,
	PropertyModalShell,
} from "./shared";

import { addFinancialStatementAsync, updateOwnerAsync } from "../../redux/user/userSlice";
import { addFinancialStatementVerifier, getPropertyAddress, getPropertyStatus } from "../../utils/verifier";
import Dropzone from "../Dropzone/Dropzone";
import FinancialStatementRow from "../TableRows/FinancialStatementRow";

function _arrayBufferToBase64(buffer) {
	var binary = "";
	var bytes = new Uint8Array(buffer);
	var len = bytes.byteLength;
	for (var i = 0; i < len; i++) {
		binary += String.fromCharCode(bytes[i]);
	}
	return window.btoa(binary);
}

export default function FinancialStatementsTab({ property, loading }) {
	const { t } = useTranslation();
	const isArchived = property.archived;
	const initialPaymentInformation = {
		name: "",
		propertyId: property.id,
		documents: [],
		description: "",
		type: "",
		year: moment().year(),
	};
	const dispatch = useDispatch();
	const [year, setYear] = useState(moment().year());

	const { isOpen, onOpen, onClose } = useDisclosure();
	const [paymentInformation, setPaymentInformation] = useState(initialPaymentInformation);
	const { onClose: onDifferentCurrencyClose } = useDisclosure();
	const [error, setError] = useState({});
	const toast = useToast();

	const onDocumentDrop = async (acceptedFiles) => {
		console.log(acceptedFiles);
		let documents = [];
		for (const file of acceptedFiles) {
			const buffer = await file.arrayBuffer();
			const documentName = file.name;
			const contentType = file.type;
			documents.push({ documentData: _arrayBufferToBase64(buffer), documentName, contentType });
		}
		setPaymentInformation({ ...paymentInformation, documents });
	};

	useEffect(() => {
		console.log(paymentInformation);
	}, [paymentInformation]);

	const addPayment = () => {
		const errors = addFinancialStatementVerifier(paymentInformation);
		const payload = {
			...paymentInformation,
		};
		console.log(payload);
		if (Object.entries(errors).length !== 0) {
			console.log(errors);
			setError(errors);
			toast({
				title: "Error encountered.",
				description: "Invalid information",
				status: "error",
				duration: 2000,
				isClosable: true,
			});
			return;
		}
		setError({});
		dispatch(addFinancialStatementAsync(payload))
			.then((r) => {
				if (r.error?.message)
					toast({
						title: "Error encountered",
						description: r.error.message,
						status: "error",
						duration: 9000,
						isClosable: true,
					});
				else {
					toast({
						title: "Success!",
						description: "Documents added!",
						status: "success",
						duration: 2000,
						isClosable: true,
					});
				}
			})
			.finally(() => {
				onClose();
				setPaymentInformation(initialPaymentInformation);
			});
	};

	const handleChange = (e) => {
		console.log("changeee");
		setPaymentInformation({ ...paymentInformation, [e.target.name]: e.target.value });
	};

	const handleCloseModal = () => {
		onClose();
		setPaymentInformation(initialPaymentInformation);
		onDifferentCurrencyClose();
		setError({});
	};

	const renderAddPaymentModal = () => (
		<PropertyModalShell
			isOpen={isOpen}
			onClose={handleCloseModal}
			size="4xl"
			icon={FiFileText}
			title={t("property.addFinancialStatement")}
			subtitle={t("property.addFinancialStatementSubtitle")}
			footer={
				<ModalFooterActions
					isLoading={loading}
					isDisabled={loading}
					onCancel={handleCloseModal}
					onConfirm={addPayment}
					confirmLabel={t("misc.save")}
				/>
			}
		>
			<SimpleGrid columns={{ base: 1, lg: 2 }} spacing={MODAL_GRID_SPACING}>
				<VStack spacing={MODAL_SECTION_SPACING} align="stretch">
					<PropertyModalSection icon={FiInfo} title={t("property.financialStatementInformation")}>
						<VStack spacing={3} align="stretch">
							<SimpleGrid columns={2} spacing={4} w="full">
								<FormControl isInvalid={error?.name}>
									<ModalFieldLabel icon={FiFileText}>{t("table.name")}</ModalFieldLabel>
									<Input size="sm" onChange={handleChange} type="text" name="name" placeholder={t("property.namePlaceholder")} borderRadius="md" />
								</FormControl>
								<FormControl isInvalid={error?.year}>
									<ModalFieldLabel icon={FiCalendar}>{t("table.year")}</ModalFieldLabel>
									<Select size="sm" onChange={handleChange} value={paymentInformation.year} name="year" placeholder={t("table.year")} borderRadius="md">
										{[...Array(10).keys()].map((i, index) => (
											<option key={index} value={moment().year() - i}>
												{moment().year() - i}
											</option>
										))}
									</Select>
								</FormControl>
							</SimpleGrid>
							<FormControl isInvalid={error?.type}>
								<ModalFieldLabel icon={FiFileText}>{t("table.type")}</ModalFieldLabel>
								<Select size="sm" onChange={handleChange} name="type" placeholder={t("table.type")} borderRadius="md">
									<option value="statement">{t("table.statement")}</option>
									<option value="c168">{t("table.c168")}</option>
								</Select>
							</FormControl>
							<FormControl isInvalid={error?.description}>
								<ModalFieldLabel icon={FiFileText}>{t("table.description")}</ModalFieldLabel>
								<Textarea
									size="sm"
									name="description"
									placeholder={t("property.descriptionPlaceholder")}
									value={paymentInformation.description}
									onChange={handleChange}
									minH="100px"
									resize="vertical"
									borderRadius="md"
								/>
							</FormControl>
						</VStack>
					</PropertyModalSection>
				</VStack>
				<VStack spacing={MODAL_SECTION_SPACING} align="stretch">
					<PropertyModalSection icon={FiUpload} title={t("property.documentsLabel")}>
						<Dropzone
							onDrop={onDocumentDrop}
							accept={{
								"application/pdf": [".pdf"],
								"application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
								"application/msword": [".doc"],
							}}
							description={t("property.uploadDocumentsDescription")}
						/>
					</PropertyModalSection>
				</VStack>
			</SimpleGrid>
		</PropertyModalShell>
	);

	return (
		<Stack spacing={4}>
			<Box>
				<Tooltip isDisabled={getPropertyStatus(property) !== "inactive"} label="Add contracts to enable">
					<Button onClick={onOpen} colorScheme="blue" isDisabled={getPropertyStatus(property) === "inactive" || isArchived}>
						{t("property.addFinancialStatement")}
					</Button>
				</Tooltip>
			</Box>
			<Box>
				<Checkbox
					isChecked={property.owner?.handlesOwnFinancialStatements}
					onChange={(e) => {
						console.log("handles own financial statements:", e.target.checked);
						dispatch(updateOwnerAsync({ type: "handlesOwn", handlesOwnFinancialStatements: e.target.checked, ownerEmail: property.owner.email }));
					}}
				>
					{t("property.ownerHandlesFinancials")}
				</Checkbox>
			</Box>

			<Divider />
			{renderAddPaymentModal()}
			<Flex direction="row" justifyContent={"space-around"}>
				<Button
					onClick={() => {
						setYear(year - 1);
					}}
				>
					{t("table.previousYear")}
				</Button>
				<Text>{year}</Text>
				<Button
					isDisabled={year === moment().year()}
					onClick={() => {
						setYear(year + 1);
					}}
				>
					{t("table.nextYear")}
				</Button>
			</Flex>
			<Box overflowX="auto">
				<Table variant="simple" size="sm">
					<Thead>
						<Tr my=".8rem" pl="0px">
							<Th pl="0px">{t("table.addedDate")}</Th>
							<Th>{t("table.name")}</Th>
							<Th>{t("table.year")}</Th>
							<Th>{t("table.type")}</Th>
							<Th>{t("table.description")}</Th>
							<Th></Th>
						</Tr>
					</Thead>
					<Tbody>
						{property.financialStatements
							?.filter((f) => Number(f.year) === year)
							.map((row, index) => {
								return (
									<FinancialStatementRow
										id={row.id}
										key={index}
										name={row.name}
										date={row.addedDate}
										address={getPropertyAddress(property)}
										paymentDate={row.paymentDate}
										code={row.number}
										amount={row.amount}
										description={row.description}
										type={row.type}
										status={row.paymentDate ? "paid" : "due"}
										logo={row.logo}
										format={row.format}
										method={row.paymentMethod}
										documents={row.documents}
										paymentDirection={row.paymentDirection}
										property={property}
										currency={row.currency}
										active={row.active}
										year={row.year}
									/>
								);
							})}
						{property.financialStatements.length === 0 && (
							<Tr>
								<Td colSpan={6}>{t("table.noData")}</Td>
							</Tr>
						)}
					</Tbody>
				</Table>
			</Box>
		</Stack>
	);
}
